/**
 * ChannelHubTab — Simplified channel management.
 * Create channels with: Name, Logo, Color, YouTube Link, Auto-Sync Interval.
 * View/edit channel broadcasts inline. Manage per-channel pre/mid-roll ads.
 */
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash2, Edit, Check, X, Youtube, Clock, Eye, EyeOff, Loader2,
  RefreshCw, ChevronDown, ChevronUp, Save, Video, Upload, Image as ImageIcon,
  Play, Pause, Settings, SkipForward
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "./utils";
import ImageUpload from "./ImageUpload";
import { TeamFormSection } from "./broadcasts/TeamFormSection";

/* ── Create / Edit Form ── */
const ChannelForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: initial?.name || "",
    logo_url: initial?.logo_url || "",
    primary_color: initial?.primary_color || "#FFFFFF",
    youtube_link: initial?.youtube_link || "",
    sync_interval_hours: initial?.sync_interval_hours ?? 6,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Channel name is required"); return; }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900/60 border border-white/10 p-5 space-y-4" data-testid="channel-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Channel Name</label>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="RRAC Football" className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" data-testid="channel-name-input" />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Channel Color</label>
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
              className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer" data-testid="channel-color-input" />
            <input type="text" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
              className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-2 text-sm font-mono" placeholder="#FFFFFF" />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Channel Logo</label>
        <ImageUpload value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} label="Logo" type="logo" />
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">YouTube Channel Link</label>
        <div className="flex items-center gap-2">
          <Youtube className="w-4 h-4 text-red-500 shrink-0" />
          <input type="text" value={form.youtube_link} onChange={(e) => setForm({ ...form, youtube_link: e.target.value })}
            placeholder="https://www.youtube.com/@ChannelHandle"
            className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" data-testid="channel-youtube-input" />
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Accepts youtube.com/@handle, /channel/UC..., or raw channel ID</p>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Auto-Sync Interval</label>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-zinc-500 shrink-0" />
          <select value={form.sync_interval_hours} onChange={(e) => setForm({ ...form, sync_interval_hours: parseInt(e.target.value) })}
            className="bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" data-testid="channel-sync-interval-select">
            <option value={1}>Every 1 hour</option>
            <option value={3}>Every 3 hours</option>
            <option value={6}>Every 6 hours</option>
            <option value={12}>Every 12 hours</option>
            <option value={24}>Every 24 hours</option>
          </select>
          <span className="text-zinc-600 text-xs">Pulls upcoming broadcasts from YouTube</span>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving}
          className="flex items-center gap-2 bg-white text-black px-5 py-2 text-sm font-bold hover:bg-zinc-200 transition-colors" data-testid="channel-save-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : initial ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          {initial ? "Save Changes" : "Create Channel"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="flex items-center gap-2 text-zinc-500 hover:text-white px-4 py-2 text-sm transition-colors">
            <X className="w-3 h-3" /> Cancel
          </button>
        )}
      </div>
    </form>
  );
};

/* ── Broadcast Editor Row ── */
const BroadcastEditRow = ({ broadcast, teams, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [title, setTitle] = useState(broadcast.title || "");
  const [homeTeam, setHomeTeam] = useState({
    name: broadcast.home_team?.name || "",
    abbreviation: broadcast.home_team?.abbreviation || "",
    mascot: broadcast.home_team?.mascot || "",
    color: broadcast.home_team?.color || broadcast.home_team?.primary_color || "#FFFFFF",
    logo_url: broadcast.home_team?.logo_url || "",
  });
  const [awayTeam, setAwayTeam] = useState({
    name: broadcast.away_team?.name || "",
    abbreviation: broadcast.away_team?.abbreviation || "",
    mascot: broadcast.away_team?.mascot || "",
    color: broadcast.away_team?.color || broadcast.away_team?.primary_color || "#FFFFFF",
    logo_url: broadcast.away_team?.logo_url || "",
  });

  const selectTeam = (teamId, isHome) => {
    const t = teams.find((t) => t.id === teamId);
    if (!t) return;
    const data = {
      name: t.name,
      abbreviation: t.abbreviation || "",
      mascot: t.mascot || "",
      color: t.primary_color || "#FFFFFF",
      logo_url: t.logo_url || "",
    };
    if (isHome) setHomeTeam(data);
    else setAwayTeam(data);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/broadcasts/schedule/${broadcast.id}`, {
        title,
        home_team: homeTeam,
        away_team: awayTeam,
      }, { headers: getAuthHeader() });
      toast.success("Broadcast updated");
      setEditing(false);
      onSaved();
    } catch {
      toast.error("Failed to update broadcast");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${broadcast.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/broadcasts/schedule/${broadcast.id}`, { headers: getAuthHeader() });
      toast.success("Broadcast deleted");
      onSaved();
    } catch {
      toast.error("Failed to delete broadcast");
    } finally {
      setDeleting(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return "TBD";
    const [h, m] = time.split(":");
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-3 py-2 px-3 border-b border-white/5 last:border-0 group" data-testid={`broadcast-row-${broadcast.id}`}>
        <div className="w-24 shrink-0 text-[10px] text-zinc-600">
          {broadcast.scheduled_date && new Date(broadcast.scheduled_date + "T00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          {" "}{formatTime(broadcast.scheduled_time)}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm truncate block">{broadcast.title}</span>
          {(broadcast.home_team?.name || broadcast.away_team?.name) && (
            <span className="text-[10px] text-zinc-600">
              {broadcast.away_team?.name || "TBD"} @ {broadcast.home_team?.name || "TBD"}
            </span>
          )}
        </div>
        {broadcast.auto_scheduled && (
          <span className="text-[9px] text-zinc-700 bg-zinc-800 px-1.5 py-0.5 shrink-0">AUTO</span>
        )}
        <button onClick={() => setEditing(true)}
          className="p-1 text-zinc-600 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
          title="Edit" data-testid={`edit-broadcast-${broadcast.id}`}>
          <Edit className="w-3.5 h-3.5" />
        </button>
        <button onClick={handleDelete} disabled={deleting}
          className="p-1 text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
          title="Delete" data-testid={`delete-broadcast-${broadcast.id}`}>
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 border-b border-white/5 bg-zinc-800/30 space-y-3" data-testid={`broadcast-edit-${broadcast.id}`}>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase mb-1">Title</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm" data-testid="broadcast-title-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamFormSection
          label="Home Team"
          team={homeTeam}
          onChange={setHomeTeam}
          teams={teams}
          onSelectTeam={(id) => selectTeam(id, true)}
          testIdPrefix="ch-home"
        />
        <TeamFormSection
          label="Away Team"
          team={awayTeam}
          onChange={setAwayTeam}
          teams={teams}
          onSelectTeam={(id) => selectTeam(id, false)}
          testIdPrefix="ch-away"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 bg-white text-black px-4 py-1.5 text-sm font-bold hover:bg-zinc-200 transition-colors" data-testid="save-broadcast-btn">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
        </button>
        <button onClick={() => setEditing(false)} className="text-zinc-500 hover:text-white px-3 py-1.5 text-sm transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

/* ── Channel Broadcasts Panel ── */
const ChannelBroadcastsPanel = ({ channelId, teams }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBroadcasts = async () => {
    try {
      const res = await axios.get(`${API}/channels/${channelId}`, { headers: getAuthHeader() });
      const ch = res.data;
      const all = [
        ...(ch.upcoming_broadcasts || []),
        ...(ch.past_broadcasts || []),
      ].sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || "") || (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
      setBroadcasts(all);
    } catch {
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBroadcasts(); }, [channelId]);

  if (loading) {
    return <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600 mx-auto" /></div>;
  }

  if (broadcasts.length === 0) {
    return <div className="py-4 text-center text-zinc-600 text-xs">No broadcasts scheduled for this channel</div>;
  }

  return (
    <div className="bg-black/20 border-t border-white/5" data-testid="channel-broadcasts-panel">
      {broadcasts.map((b) => (
        <BroadcastEditRow key={b.id} broadcast={b} teams={teams} onSaved={fetchBroadcasts} />
      ))}
    </div>
  );
};

/* ── Channel Ads Panel ── */
const ChannelAdsPanel = ({ channelId }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAd, setEditingAd] = useState(null);
  const videoRef = useRef(null);
  const billboardRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${API}/video-ads?channel_id=${channelId}`, { headers: getAuthHeader() });
      setAds(res.data || []);
    } catch {
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAds(); }, [channelId]);

  const handleUpload = async (file, isBillboard) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name.replace(/\.[^/.]+$/, ""));
    if (isBillboard) {
      formData.append("ad_type", "pre_roll");
      formData.append("ad_format", "billboard");
      formData.append("duration_seconds", "10");
      formData.append("is_skippable", "true");
      formData.append("skip_after_seconds", "3");
    } else {
      formData.append("ad_type", "pre_roll");
      formData.append("duration_seconds", "15");
      formData.append("is_skippable", "true");
      formData.append("skip_after_seconds", "5");
    }
    try {
      const endpoint = isBillboard ? `${API}/video-ads/upload-billboard` : `${API}/video-ads/upload`;
      const res = await axios.post(endpoint, formData, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      // Assign to this channel
      const newAd = res.data.ad;
      await axios.put(`${API}/video-ads/${newAd.id}`, { channel_ids: [channelId] }, { headers: getAuthHeader() });
      toast.success("Ad uploaded and assigned to channel");
      fetchAds();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (videoRef.current) videoRef.current.value = "";
      if (billboardRef.current) billboardRef.current.value = "";
    }
  };

  const toggleActive = async (ad) => {
    try {
      const res = await axios.post(`${API}/video-ads/${ad.id}/toggle`, {}, { headers: getAuthHeader() });
      setAds(ads.map((a) => (a.id === ad.id ? { ...a, is_active: res.data.is_active } : a)));
    } catch { toast.error("Failed to toggle"); }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.name}"?`)) return;
    try {
      await axios.delete(`${API}/video-ads/${ad.id}`, { headers: getAuthHeader() });
      toast.success("Ad deleted");
      fetchAds();
    } catch { toast.error("Failed to delete"); }
  };

  const saveEdit = async () => {
    if (!editingAd) return;
    try {
      await axios.put(`${API}/video-ads/${editingAd.id}`, {
        name: editingAd.name,
        ad_type: editingAd.ad_type,
        duration_seconds: editingAd.duration_seconds,
        is_skippable: editingAd.is_skippable,
        skip_after_seconds: editingAd.skip_after_seconds,
        sponsor_name: editingAd.sponsor_name,
        click_url: editingAd.click_url,
        priority: editingAd.priority,
      }, { headers: getAuthHeader() });
      toast.success("Ad updated");
      setEditingAd(null);
      fetchAds();
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600 mx-auto" /></div>;

  return (
    <div className="bg-black/20 border-t border-white/5 p-4 space-y-3" data-testid="channel-ads-panel">
      {/* Upload buttons */}
      <div className="flex items-center gap-2">
        <input type="file" ref={videoRef} accept="video/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0], false)} />
        <input type="file" ref={billboardRef} accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0], true)} />
        <button onClick={() => videoRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 bg-white text-black px-3 py-1.5 text-xs font-bold hover:bg-zinc-200 transition-colors" data-testid="channel-upload-video-ad">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Video Ad
        </button>
        <button onClick={() => billboardRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1.5 bg-zinc-800 text-white px-3 py-1.5 text-xs font-bold hover:bg-zinc-700 border border-white/10 transition-colors" data-testid="channel-upload-billboard">
          <ImageIcon className="w-3 h-3" /> Billboard
        </button>
        <span className="text-[10px] text-zinc-600 ml-2">{ads.length} ad{ads.length !== 1 ? "s" : ""} on this channel</span>
      </div>

      {/* Ads list */}
      {ads.length === 0 ? (
        <div className="py-4 text-center text-zinc-600 text-xs">
          <Video className="w-6 h-6 mx-auto mb-1 text-zinc-700" />
          No ads assigned to this channel yet
        </div>
      ) : (
        <div className="space-y-1">
          {ads.map((ad) => (
            <div key={ad.id} className={`flex items-center gap-3 py-2 px-3 border border-white/5 group hover:border-white/10 transition-colors ${!ad.is_active ? "opacity-50" : ""}`} data-testid={`channel-ad-${ad.id}`}>
              {/* Preview thumbnail */}
              <div className="w-16 h-10 bg-black shrink-0 overflow-hidden relative">
                {ad.ad_format === "billboard" && ad.image_url ? (
                  <img src={ad.image_url.startsWith("/api") ? `${API.replace("/api", "")}${ad.image_url}` : ad.image_url} alt="" className="w-full h-full object-cover" />
                ) : ad.video_url ? (
                  <video src={ad.video_url.startsWith("/api") ? `${API.replace("/api", "")}${ad.video_url}` : ad.video_url} className="w-full h-full object-cover" muted />
                ) : (
                  <div className="w-full h-full bg-zinc-800 flex items-center justify-center"><Video className="w-4 h-4 text-zinc-600" /></div>
                )}
                <span className="absolute bottom-0 right-0 bg-black/80 text-[8px] text-white px-1">{ad.duration_seconds}s</span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate">{ad.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[9px] font-bold uppercase px-1 py-px ${ad.ad_type === "pre_roll" ? "bg-orange-500/20 text-orange-400" : ad.ad_type === "mid_roll" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}>
                    {ad.ad_type?.replace("_", "-") || "pre-roll"}
                  </span>
                  {ad.ad_format === "billboard" && <span className="text-[9px] font-bold uppercase px-1 py-px bg-purple-500/20 text-purple-300">Billboard</span>}
                  {ad.sponsor_name && <span className="text-[9px] text-zinc-600">{ad.sponsor_name}</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => toggleActive(ad)} className="p-1 text-zinc-500 hover:text-white" title={ad.is_active ? "Pause" : "Activate"}>
                  {ad.is_active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                </button>
                <button onClick={() => setEditingAd({ ...ad })} className="p-1 text-zinc-500 hover:text-white" title="Edit" data-testid={`edit-channel-ad-${ad.id}`}>
                  <Settings className="w-3 h-3" />
                </button>
                <button onClick={() => deleteAd(ad)} className="p-1 text-zinc-500 hover:text-red-400" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inline edit form */}
      {editingAd && (
        <div className="bg-zinc-900/80 border border-white/10 p-4 space-y-3" data-testid="channel-ad-edit-form">
          <div className="flex items-center justify-between">
            <h4 className="text-white text-xs font-bold uppercase">Edit Ad</h4>
            <button onClick={() => setEditingAd(null)} className="text-zinc-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Name</label>
              <input type="text" value={editingAd.name || ""} onChange={(e) => setEditingAd({ ...editingAd, name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Type</label>
              <select value={editingAd.ad_type || "pre_roll"} onChange={(e) => setEditingAd({ ...editingAd, ad_type: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs">
                <option value="pre_roll">Pre-Roll</option>
                <option value="mid_roll">Mid-Roll</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Duration (sec)</label>
              <input type="number" value={editingAd.duration_seconds || 15} onChange={(e) => setEditingAd({ ...editingAd, duration_seconds: parseInt(e.target.value) || 15 })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" min={3} max={120} />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Priority</label>
              <input type="number" value={editingAd.priority || 0} onChange={(e) => setEditingAd({ ...editingAd, priority: parseInt(e.target.value) || 0 })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" min={0} max={100} />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Sponsor</label>
              <input type="text" value={editingAd.sponsor_name || ""} onChange={(e) => setEditingAd({ ...editingAd, sponsor_name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Click URL</label>
              <input type="text" value={editingAd.click_url || ""} onChange={(e) => setEditingAd({ ...editingAd, click_url: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" placeholder="https://..." />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={editingAd.is_skippable !== false} onChange={(e) => setEditingAd({ ...editingAd, is_skippable: e.target.checked })}
                className="accent-orange-500" /> Skippable
            </label>
            {editingAd.is_skippable && (
              <div className="flex items-center gap-1 text-xs text-zinc-400">
                after <input type="number" value={editingAd.skip_after_seconds || 5} onChange={(e) => setEditingAd({ ...editingAd, skip_after_seconds: parseInt(e.target.value) || 5 })}
                  className="w-12 bg-black/40 border border-white/10 text-white px-1 py-0.5 text-xs text-center" min={0} max={30} /> sec
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={saveEdit} className="flex items-center gap-1.5 bg-white text-black px-4 py-1.5 text-xs font-bold hover:bg-zinc-200 transition-colors" data-testid="save-channel-ad-btn">
              <Save className="w-3 h-3" /> Save
            </button>
            <button onClick={() => setEditingAd(null)} className="text-zinc-500 hover:text-white px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Channel Banners Panel ── */
const ChannelBannersPanel = ({ channelId }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newBanner, setNewBanner] = useState({ name: "", image_url: "", link_url: "" });
  const [saving, setSaving] = useState(false);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API}/channels/${channelId}`, { headers: getAuthHeader() });
      setBanners(res.data?.banners || []);
    } catch { setBanners([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBanners(); }, [channelId]);

  const addBanner = async () => {
    if (!newBanner.image_url) { toast.error("Image URL is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/channels/${channelId}/banners`, newBanner, { headers: getAuthHeader() });
      toast.success("Banner added");
      setAdding(false);
      setNewBanner({ name: "", image_url: "", link_url: "" });
      fetchBanners();
    } catch { toast.error("Failed to add banner"); }
    finally { setSaving(false); }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await axios.delete(`${API}/channels/${channelId}/banners/${bannerId}`, { headers: getAuthHeader() });
      toast.success("Banner deleted");
      fetchBanners();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleBanner = async (banner) => {
    try {
      await axios.put(`${API}/channels/${channelId}/banners/${banner.id}`, {
        ...banner, is_active: !banner.is_active,
      }, { headers: getAuthHeader() });
      fetchBanners();
    } catch { toast.error("Failed to toggle"); }
  };

  if (loading) return <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600 mx-auto" /></div>;

  return (
    <div className="bg-black/20 border-t border-white/5 p-4 space-y-3" data-testid="channel-banners-panel">
      {/* Add button */}
      <div className="flex items-center gap-2">
        <button onClick={() => setAdding(!adding)}
          className="flex items-center gap-1.5 bg-white text-black px-3 py-1.5 text-xs font-bold hover:bg-zinc-200 transition-colors" data-testid="add-channel-banner-btn">
          <Plus className="w-3 h-3" /> Add Banner
        </button>
        <span className="text-[10px] text-zinc-600">{banners.length} banner{banners.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Add form */}
      {adding && (
        <div className="bg-zinc-900/80 border border-white/10 p-4 space-y-3" data-testid="add-banner-form">
          <div>
            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Banner Image</label>
            <ImageUpload value={newBanner.image_url} onChange={(url) => setNewBanner({ ...newBanner, image_url: url })} label="Banner" type="banner" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Name (optional)</label>
              <input type="text" value={newBanner.name} onChange={(e) => setNewBanner({ ...newBanner, name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" placeholder="Sponsor Banner" />
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Click URL (optional)</label>
              <input type="text" value={newBanner.link_url} onChange={(e) => setNewBanner({ ...newBanner, link_url: e.target.value })}
                className="w-full bg-black/40 border border-white/10 text-white px-2 py-1.5 text-xs" placeholder="https://..." />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addBanner} disabled={saving}
              className="flex items-center gap-1.5 bg-white text-black px-4 py-1.5 text-xs font-bold hover:bg-zinc-200 transition-colors" data-testid="save-banner-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
            </button>
            <button onClick={() => setAdding(false)} className="text-zinc-500 hover:text-white px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      )}

      {/* Banner list */}
      {banners.length === 0 && !adding ? (
        <div className="py-4 text-center text-zinc-600 text-xs">
          <ImageIcon className="w-6 h-6 mx-auto mb-1 text-zinc-700" />
          No banners on this channel yet
        </div>
      ) : (
        <div className="space-y-2">
          {banners.map((b) => (
            <div key={b.id} className={`flex items-center gap-3 p-2 border border-white/5 group hover:border-white/10 transition-colors ${!b.is_active ? "opacity-40" : ""}`}
              data-testid={`channel-banner-${b.id}`}>
              <div className="w-28 h-14 bg-black shrink-0 overflow-hidden">
                <img src={b.image_url} alt={b.name || "Banner"} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate">{b.name || "Banner"}</p>
                {b.link_url && <p className="text-zinc-600 text-[10px] truncate">{b.link_url}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => toggleBanner(b)} className="p-1 text-zinc-500 hover:text-white" title={b.is_active ? "Disable" : "Enable"}>
                  {b.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </button>
                <button onClick={() => deleteBanner(b.id)} className="p-1 text-zinc-500 hover:text-red-400" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Channel Card ── */
const ChannelCard = ({ channel, onEdit, onDelete, onToggleVisibility, onSync, teams }) => {
  const color = channel.primary_color || "#FFFFFF";
  const isHidden = channel.is_visible === false;
  const [syncing, setSyncing] = useState(false);
  const [expandTab, setExpandTab] = useState(null); // null | "broadcasts" | "ads" | "banners"

  const handleSync = async () => {
    setSyncing(true);
    try { await onSync(channel); } finally { setSyncing(false); }
  };

  return (
    <div data-testid={`channel-card-${channel.channel_id}`}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900/60 border border-white/10 p-4 flex items-center gap-4 group hover:border-white/20 transition-colors"
        style={{ borderLeftColor: color, borderLeftWidth: "3px" }}
      >
        <div className="w-12 h-12 bg-black/40 flex items-center justify-center shrink-0 overflow-hidden">
          {channel.logo_url ? (
            <img src={channel.logo_url} alt="" className="w-full h-full object-contain" />
          ) : (
            <span className="text-lg font-bold" style={{ color }}>{channel.name?.[0] || "?"}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white text-sm font-bold truncate">{channel.name}</h4>
            {isHidden && <span className="text-[10px] text-zinc-600 bg-zinc-800 px-1.5 py-0.5">HIDDEN</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
            {channel.youtube_link && (
              <span className="flex items-center gap-1"><Youtube className="w-3 h-3 text-red-500" /> YouTube linked</span>
            )}
            {channel.sync_interval_hours && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Every {channel.sync_interval_hours}h</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
          {/* View Broadcasts toggle */}
          <button onClick={() => setExpandTab(expandTab === "broadcasts" ? null : "broadcasts")}
            className={`p-1.5 transition-colors text-xs flex items-center gap-1 ${expandTab === "broadcasts" ? "text-white" : "text-zinc-500 hover:text-white"}`}
            title="View Broadcasts" data-testid="channel-view-broadcasts-btn">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandTab === "broadcasts" ? "rotate-180" : ""}`} />
          </button>
          {/* Ads toggle */}
          <button onClick={() => setExpandTab(expandTab === "ads" ? null : "ads")}
            className={`p-1.5 transition-colors ${expandTab === "ads" ? "text-orange-400" : "text-zinc-500 hover:text-orange-400"}`}
            title="Channel Ads" data-testid="channel-view-ads-btn">
            <Video className="w-3.5 h-3.5" />
          </button>
          {/* Banners toggle */}
          <button onClick={() => setExpandTab(expandTab === "banners" ? null : "banners")}
            className={`p-1.5 transition-colors ${expandTab === "banners" ? "text-cyan-400" : "text-zinc-500 hover:text-cyan-400"}`}
            title="Channel Banners" data-testid="channel-view-banners-btn">
            <ImageIcon className="w-3.5 h-3.5" />
          </button>
          {channel.youtube_link && (
            <button onClick={handleSync} disabled={syncing}
              className="p-1.5 text-zinc-500 hover:text-green-400 transition-colors" title="Sync YouTube Now" data-testid="channel-sync-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </button>
          )}
          <button onClick={() => onToggleVisibility(channel)}
            className="p-1.5 text-zinc-500 hover:text-white transition-colors" title={isHidden ? "Show" : "Hide"} data-testid="channel-toggle-visibility-btn">
            {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onEdit(channel)}
            className="p-1.5 text-zinc-500 hover:text-white transition-colors" title="Edit" data-testid="channel-edit-btn">
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(channel)}
            className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" title="Delete" data-testid="channel-delete-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </motion.div>
      {/* Expandable panel */}
      <AnimatePresence>
        {expandTab && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            {/* Tab header */}
            <div className="flex gap-0 bg-zinc-900/40 border-t border-white/5">
              <button onClick={() => setExpandTab("broadcasts")}
                className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${expandTab === "broadcasts" ? "text-white border-b-2 border-white" : "text-zinc-600 hover:text-zinc-400"}`}
                data-testid="tab-broadcasts">
                Broadcasts
              </button>
              <button onClick={() => setExpandTab("ads")}
                className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${expandTab === "ads" ? "text-orange-400 border-b-2 border-orange-400" : "text-zinc-600 hover:text-zinc-400"}`}
                data-testid="tab-ads">
                Pre/Mid-Roll Ads
              </button>
              <button onClick={() => setExpandTab("banners")}
                className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider transition-colors ${expandTab === "banners" ? "text-cyan-400 border-b-2 border-cyan-400" : "text-zinc-600 hover:text-zinc-400"}`}
                data-testid="tab-banners">
                Banners
              </button>
            </div>
            {expandTab === "broadcasts" && <ChannelBroadcastsPanel channelId={channel.channel_id} teams={teams} />}
            {expandTab === "ads" && <ChannelAdsPanel channelId={channel.channel_id} />}
            {expandTab === "banners" && <ChannelBannersPanel channelId={channel.channel_id} />}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Main Tab ── */
export const ChannelHubTab = ({ channels, onRefresh }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [teams, setTeams] = useState([]);

  // Fetch teams for the team selector dropdowns
  useEffect(() => {
    axios.get(`${API}/teams`, { headers: getAuthHeader() })
      .then((res) => setTeams(res.data || []))
      .catch(() => {});
  }, []);

  const handleCreate = async (form) => {
    try {
      await axios.post(`${API}/channels`, form, { headers: getAuthHeader() });
      toast.success("Channel created");
      setShowCreate(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create channel");
    }
  };

  const handleUpdate = async (channel, form) => {
    try {
      await axios.put(`${API}/channels/${channel.channel_id}/branding`, form, { headers: getAuthHeader() });
      toast.success("Channel updated");
      setEditingId(null);
      onRefresh();
    } catch {
      toast.error("Failed to update channel");
    }
  };

  const handleDelete = async (channel) => {
    try {
      await axios.delete(`${API}/channels/${channel.channel_id}`, { headers: getAuthHeader() });
      toast.success("Channel deleted");
      setConfirmDelete(null);
      onRefresh();
    } catch {
      toast.error("Failed to delete channel");
    }
  };

  const handleToggleVisibility = async (channel) => {
    try {
      await axios.put(`${API}/channels/${channel.channel_id}/branding`, { is_visible: channel.is_visible === false }, { headers: getAuthHeader() });
      onRefresh();
    } catch {
      toast.error("Failed to toggle visibility");
    }
  };

  const handleSync = async (channel) => {
    try {
      const res = await axios.post(`${API}/channels/${channel.channel_id}/sync-youtube`, {}, { headers: getAuthHeader() });
      const s = res.data.stats;
      toast.success(`Synced: ${s.created} created, ${s.skipped} skipped, ${s.completed} completed`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
  };

  const sortedChannels = [...(channels || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="space-y-6" data-testid="channel-hub-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-lg font-bold">Channels</h2>
          <p className="text-zinc-500 text-xs mt-1">
            Create and manage broadcast channels. Expand a channel to view and edit its broadcasts.
          </p>
        </div>
        <button onClick={() => { setShowCreate(!showCreate); setEditingId(null); }}
          className="flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-bold hover:bg-zinc-200 transition-colors" data-testid="create-channel-btn">
          <Plus className="w-4 h-4" /> New Channel
        </button>
      </div>

      {showCreate && <ChannelForm onSave={handleCreate} onCancel={() => setShowCreate(false)} />}

      <div className="space-y-2">
        {sortedChannels.map((ch) => (
          <div key={ch.channel_id}>
            {editingId === ch.channel_id ? (
              <ChannelForm initial={ch} onSave={(form) => handleUpdate(ch, form)} onCancel={() => setEditingId(null)} />
            ) : (
              <ChannelCard
                channel={ch}
                onEdit={(c) => { setEditingId(c.channel_id); setShowCreate(false); }}
                onDelete={(c) => setConfirmDelete(c)}
                onToggleVisibility={handleToggleVisibility}
                onSync={handleSync}
                teams={teams}
              />
            )}
          </div>
        ))}
        {sortedChannels.length === 0 && !showCreate && (
          <div className="text-center py-12">
            <p className="text-zinc-600 text-sm">No channels yet.</p>
            <button onClick={() => setShowCreate(true)} className="mt-3 text-zinc-400 hover:text-white text-xs transition-colors">
              Create your first channel
            </button>
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setConfirmDelete(null)}>
          <div className="bg-zinc-900 border border-white/10 p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <h3 className="text-white text-sm font-bold">Delete "{confirmDelete.name}"?</h3>
            <p className="text-zinc-500 text-xs">This will also delete all associated broadcasts. This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="text-zinc-500 hover:text-white px-4 py-2 text-sm transition-colors">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm transition-colors" data-testid="confirm-delete-btn">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelHubTab;
